import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { HeaderWidget, HistoryList, themeColor } from "@brandclub/bc-widgets";
import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
/* empty css                       */
import { IconHome, IconChevronLeft, IconCompose } from "./ConciergeIcons.js";
const fetchConversations = async (apiBaseUrl) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers
    }
  });
  if (!response.ok) {
    throw new Error("Failed to fetch conversations");
  }
  return response.json();
};
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, textColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const handleClick = (e) => {
    e.stopPropagation();
    onClick == null ? void 0 : onClick();
  };
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: handleClick,
        "aria-label": ariaLabel,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full",
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-5", children: icon }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
              style: {
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? "auto" : 0,
                color: textColor
              },
              children: label
            }
          )
        ]
      }
    ),
    !isExpanded && isHovered && /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-primary tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
        style: {
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: "18px"
        },
        children: label
      }
    )
  ] });
};
const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  currentConversationId,
  userProfile,
  apiBaseUrl = "/api-rewards",
  onSignOutClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle
}) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const isControlled = isExpandedProp !== void 0;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;
  const theme = useTheme();
  const handleToggle = () => {
    if (isControlled) {
      onToggle == null ? void 0 : onToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ["conversations", apiBaseUrl],
    queryFn: () => fetchConversations(apiBaseUrl),
    enabled: isExpanded,
    staleTime: 5 * 60 * 1e3,
    gcTime: 10 * 60 * 1e3
  });
  const getInitials = () => {
    const firstName = (userProfile == null ? void 0 : userProfile.firstName) || "";
    const lastName = (userProfile == null ? void 0 : userProfile.lastName) || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    return "";
  };
  const handleConversationClick = (conversationId) => {
    onConversationSelect == null ? void 0 : onConversationSelect(conversationId);
  };
  const initials = getInitials();
  const isSignedIn = (userProfile == null ? void 0 : userProfile.isSignedIn) ?? false;
  const [avatarHovered, setAvatarHovered] = useState(false);
  return /* @__PURE__ */ jsxs(
    "aside",
    {
      role: "complementary",
      "aria-expanded": isExpanded,
      onClick: !isExpanded ? handleToggle : void 0,
      className: "tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full tw:border-r tw:border-[#dcdddc]",
      style: {
        backgroundColor: theme.palette.background.default,
        width: isExpanded ? 248 : 40,
        transition: "width 0.25s ease-in-out",
        cursor: isExpanded ? void 0 : "pointer"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "tw:flex tw:items-center tw:justify-between tw:shrink-0 tw:px-2.5 tw:pt-3 tw:pb-2",
            onClick: (e) => e.stopPropagation(),
            children: [
              isExpanded ? brandLogoUrl ? /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: (e) => {
                    e.stopPropagation();
                    onHomeClick == null ? void 0 : onHomeClick();
                  },
                  className: "tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer",
                  "aria-label": "Go to home",
                  children: /* @__PURE__ */ jsx("img", { src: brandLogoUrl, alt: "Brand", className: "tw:h-12 tw:w-auto tw:max-w-[160px] tw:object-contain" })
                }
              ) : /* @__PURE__ */ jsx("div", { className: "tw:w-10" }) : /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: (e) => {
                    e.stopPropagation();
                    onHomeClick == null ? void 0 : onHomeClick();
                  },
                  className: "tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:transition-colors hover:tw:bg-black/5",
                  "aria-label": "Go to home",
                  children: /* @__PURE__ */ jsx(IconHome, { size: 22, style: { color: theme.palette.text.primary } })
                }
              ),
              isExpanded && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleToggle();
                  },
                  className: "tw:flex tw:items-center tw:justify-center tw:size-8 tw:rounded tw:bg-transparent tw:transition-colors hover:tw:bg-black/5",
                  "aria-label": "Close sidebar",
                  title: "Close sidebar",
                  children: /* @__PURE__ */ jsx(IconChevronLeft, { size: 18, style: { color: theme.palette.text.primary } })
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "tw:flex tw:flex-col tw:pt-1", children: /* @__PURE__ */ jsx(HeaderWidget, { widgetId: "concierge-sidebar-header", displayName: "Sidebar Header", children: (state, resolveColor) => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
          SidebarButton,
          {
            icon: /* @__PURE__ */ jsx(IconCompose, { size: state.iconSize - 2, style: { color: resolveColor(state.iconColor) } }),
            label: "New chat",
            onClick: onComposeClick,
            isExpanded,
            ariaLabel: "Start new chat",
            textColor: resolveColor(state.titleColor)
          }
        ) }) }) }),
        isExpanded ? /* @__PURE__ */ jsxs("div", { className: "sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:mt-4 tw:overflow-hidden", children: [
          /* @__PURE__ */ jsx("div", { className: "tw:px-2.5 tw:text-sm tw:font-medium tw:text-primary tw:shrink-0", children: "Chats" }),
          /* @__PURE__ */ jsx("div", { className: "tw:flex-1 tw:overflow-y-auto tw:mt-2", children: /* @__PURE__ */ jsx(
            HistoryList,
            {
              conversations: (data == null ? void 0 : data.conversations) || [],
              isLoading,
              error: error ? "Failed to load conversations" : null,
              onSelect: handleConversationClick,
              currentConversationId,
              widgetId: "concierge-sidebar-history",
              displayName: "Sidebar History",
              showHeader: false,
              emptyMessage: "No conversations yet",
              listContainerClassName: "sidebar-history-list",
              initialState: {
                // Customize styling for sidebar - more compact
                itemPaddingX: 8,
                itemPaddingY: 4,
                itemTitleLineClamp: 1,
                timestampMarginTop: 2,
                timestampFontSize: 11,
                drawerBgColor: themeColor("transparent")
              }
            }
          ) })
        ] }) : (
          /* Spacer - only when collapsed to push avatar to bottom */
          /* @__PURE__ */ jsx("div", { className: "tw:flex-1" })
        ),
        isSignedIn && /* @__PURE__ */ jsxs("div", { className: "tw:relative", onClick: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              "aria-label": "Sign out",
              onClick: (e) => {
                e.stopPropagation();
                onSignOutClick == null ? void 0 : onSignOutClick();
              },
              onMouseEnter: () => setAvatarHovered(true),
              onMouseLeave: () => setAvatarHovered(false),
              className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:mb-2 tw:cursor-pointer tw:w-full",
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:flex tw:items-center tw:justify-center tw:size-5 tw:rounded-full tw:text-[8px] tw:font-semibold tw:leading-[12px] tw:text-white tw:shrink-0",
                    style: { backgroundColor: theme.palette.text.primary },
                    children: initials
                  }
                ),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
                    style: {
                      opacity: isExpanded ? 1 : 0,
                      width: isExpanded ? "auto" : 0,
                      color: theme.palette.text.primary
                    },
                    children: "Sign out"
                  }
                )
              ]
            }
          ),
          !isExpanded && avatarHovered && /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-primary tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
              style: {
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "18px"
              },
              children: "Sign out"
            }
          )
        ] })
      ]
    }
  );
};
export {
  ConciergeSidebar
};
//# sourceMappingURL=ConciergeSidebar.js.map
